import { AUTH_ACTIVITY, type Page, type Role } from '@agrogruda/shared';
import {
  LOGIN_FAILED_MESSAGE,
  NAME_REQUIRED_MESSAGE,
  USERNAME_TAKEN_MESSAGE,
  USERS_ADMIN_ONLY_MESSAGE,
} from '@agrogruda/shared/schemas';

/**
 * Albanian UI strings. Unless marked NEW, every string is copied character-for-character from the prototype
 * (docs/prototype/AgroGruda_ERP_Stoku_Krejt_Nje_Rresht.html). NEW strings did not exist in the prototype
 * (first-run setup screen, login creation) — see docs/KNOWN_LIMITATIONS.md L11.
 */

type PageMeta = Readonly<{ title: string; subtitle: string }>;

/** Prototype go() map, line 1610. */
export const PAGE_META: Readonly<Record<Page, PageMeta>> = {
  dashboard: { title: 'Ballina', subtitle: 'Përmbledhja e aktivitetit të biznesit' },
  sales: { title: 'Shitje', subtitle: 'Skano barkodin ose kërko produktin dhe përfundo shitjen' },
  purchases: { title: 'Blerje', subtitle: 'Regjistro faturat hyrëse dhe shto stokun' },
  suppliers: { title: 'Furnitorët', subtitle: 'Kartelat dhe historia e blerjeve' },
  stock: {
    title: 'Stoku',
    subtitle: 'Stoku i ndarë në Lokal dhe Depo · me total automatik dhe transfer mes lokacioneve',
  },
  debts: { title: 'Borxhet', subtitle: 'Kartelat e klientëve dhe pagesat' },
  tasks: { title: 'Detyrat', subtitle: 'Punët e sotme, afatet dhe progresi i ekipit' },
  whatsapp: { title: 'WhatsApp', subtitle: 'Mesazhet e klientëve dhe përgjigjet e stafit' },
  attendance: { title: 'Pjesëmarrja', subtitle: 'Regjistrimi automatik i punëtorëve përmes Wi‑Fi / UniFi' },
  reports: { title: 'Raportet', subtitle: 'Analiza ditore, mujore dhe vjetore' },
  /** NEW — the prototype had no delivery screen; warehouse orders only showed at the till. */
  deliveries: { title: 'Dorëzimet', subtitle: 'Porositë nga depoja dhe nga dyqani — tuajat në krye' },
  /** NEW — the prototype had no settings screen; the barcode printer is configured per workstation. */
  settings: { title: 'Konfigurimet', subtitle: 'Printeri i barkodeve dhe konfigurimet e kësaj pajisjeje' },
};

/** Sidebar labels, prototype lines 904-933 (same order as PAGES). */
export const NAV_LABELS: Readonly<Record<Page, string>> = {
  dashboard: 'Ballina',
  sales: 'Shitje',
  purchases: 'Blerje',
  suppliers: 'Furnitorët',
  stock: 'Stoku',
  debts: 'Borxhet',
  tasks: 'Detyrat',
  whatsapp: 'WhatsApp',
  attendance: 'Pjesëmarrja',
  reports: 'Raportet',
  /** NEW — see PAGE_META.deliveries. */
  deliveries: 'Dorëzimet',
  /** NEW — see PAGE_META.settings. */
  settings: 'Konfigurimet',
};

/** Role select options in the users modal, prototype line 1465. */
export const ROLE_OPTIONS: Readonly<Record<Role, string>> = {
  staff: 'Staf / Menaxher & Punëtorë',
  delivery: 'Punëtor dorëzimesh — vetëm porositë e veta',
  admin: 'Drejtor / Administrator',
};

/** Option order of the prototype's `#userRole` select (staff first, then admin) — not the `ROLES` order. */
export const ROLE_OPTION_ORDER: readonly Role[] = ['staff', 'delivery', 'admin'];

export const sq = {
  app: {
    name: 'AgroGruda ERP',
    logoAlt: 'Gruda',
    /** Prototype topbar `.preview-badge` (line 950) — the generation label of the ported prototype. */
    versionBadge: 'v101 · WhatsApp Inbox',
  },
  nav: {
    menu: 'Menuja kryesore',
    users: 'Përdoruesit',
    logout: 'Dil',
    noAccess: 'Nuk ke qasje në këtë pjesë',
  },
  common: {
    save: 'Ruaj',
    close: 'Mbyll',
    cancel: 'Anulo',
    edit: 'Ndrysho',
    yes: 'Po',
    no: 'Jo',
    system: 'Sistem',
    /** NEW — title of the confirm dialog that replaces window.confirm(). */
    confirmTitle: 'Konfirmo',
  },
  auth: {
    username: 'Username',
    password: 'Fjalëkalimi',
    passwordPlaceholder: '••••••••',
    login: 'Hyr',
    invalidCredentials: LOGIN_FAILED_MESSAGE,
    welcome: (name: string): string => `Mirë se erdhe, ${name}`,
    loginActivity: AUTH_ACTIVITY.login,
    logoutActivity: AUTH_ACTIVITY.logout,
    onlyAdminManagesUsers: USERS_ADMIN_ONLY_MESSAGE,
  },
  /** NEW screen — the prototype had no first-run flow (ensureTwoAccounts seeded demo logins). */
  setup: {
    badge: 'Konfigurimi i parë',
    title: 'Krijo administratorin e parë',
    subtitle: 'Kjo hyrje ka qasje të plotë në ERP dhe krijon hyrjet e stafit.',
    fullName: 'Emri i plotë',
    confirmPassword: 'Përsërit fjalëkalimin',
    submit: 'Krijo administratorin',
    passwordsMismatch: 'Fjalëkalimet nuk përputhen',
  },
  users: {
    /** NEW — the prototype title 'Dy hyrjet e AgroGruda' assumed exactly two logins (decision D7). */
    title: 'Përdoruesit',
    subtitle: 'Vetëm Drejtori / Admini dhe Stafi i AgroGruda',
    idleTitle: 'Zgjedh njërën hyrje për ndryshim',
    editTitle: (name: string): string => `Ndrysho ${name}`,
    /** NEW */
    createTitle: 'Hyrje e re',
    /** NEW */
    addLogin: 'Shto hyrje',
    name: 'Emri *',
    namePlaceholder: 'Emri i hyrjes',
    username: 'Username *',
    usernamePlaceholder: 'admin ose staf',
    role: 'Qasja',
    employee: 'Punëtori',
    /** NEW */
    noEmployee: 'Pa punëtor',
    newPassword: 'Fjalëkalim i ri (opsional)',
    newPasswordPlaceholder: 'Lëre bosh nëse nuk ndryshon',
    /** NEW */
    passwordRequired: 'Fjalëkalimi *',
    active: 'Aktiv',
    inactive: 'Çaktivizuar',
    cancelEdit: 'Anulo ndryshimin',
    saveEdit: 'Ruaj ndryshimet',
    /** NEW */
    create: 'Krijo hyrjen',
    selectFirst: 'Zgjedh Drejtorin ose Stafin nga lista',
    nameRequired: NAME_REQUIRED_MESSAGE,
    usernameTaken: USERNAME_TAKEN_MESSAGE,
    saved: 'Hyrja u ruajt',
    updatedActivity: AUTH_ACTIVITY.userSaved,
    /** NEW — creating a login did not exist in the prototype. */
    createdActivity: 'U krijua hyrja',
    /** NEW — removing a login that is no longer needed (another login than one's own). */
    delete: 'Fshij',
    deleteTitle: 'Fshij hyrjen',
    deleteOk: 'Po, fshije',
    deleteConfirm: (name: string, username: string): string =>
      `A je i sigurt që do ta fshish hyrjen “${name}” (@${username})? Nuk do të mund të kyçet më. ` +
      'Shitjet, borxhet dhe veprimet që ka regjistruar mbesin. ' +
      'Nëse do vetëm ta ndalosh përkohësisht, hiqe “Aktiv” në vend të kësaj.',
    deleted: (name: string): string => `Hyrja “${name}” u fshi`,
    permissionNote: {
      adminLabel: 'Drejtor / Admin:',
      adminText: 'qasje e plotë, përfshirë fitimin, konfigurimet dhe menaxhimin e hyrjeve.',
      staffLabel: 'Staf:',
      staffText:
        'një hyrje e përbashkët për menaxherin dhe punëtorët për punën ditore; fitimi, konfigurimet dhe administrimi i user-ave mbeten vetëm për Drejtorin.',
      /** NEW */
      deliveryLabel: 'Punëtor dorëzimesh:',
      deliveryText:
        'sheh vetëm porositë që i janë dhënë atij dhe merr njoftim në telefon për secilën; duhet të lidhet me punëtorin.',
    },
  },
  errors: {
    /** NEW — the API could not be reached. */
    serviceUnavailable: 'Shërbimi nuk është i arritshëm. Provo përsëri.',
    /** NEW — generic failure of a save through the API. */
    requestFailed: 'Ruajtja dështoi. Provo përsëri.',
  },
} as const;

export type Sq = typeof sq;

/**
 * Interpolates `{name}` placeholders: t('Mirë se erdhe, {name}', { name: 'Flori' }).
 * Unknown placeholders are left untouched so a missing value stays visible instead of silently blank.
 */
export function t(template: string, params: Readonly<Record<string, string | number>> = {}): string {
  return template.replace(/\{(\w+)\}/g, (match: string, key: string) => {
    const value = params[key];
    return value === undefined ? match : String(value);
  });
}

/** Prototype `@username · Aktiv|Çaktivizuar` line under a user name (renderAuthUsers, line 3116). */
export function userStatusLine(username: string, active: boolean): string {
  return `@${username} · ${active ? sq.users.active : sq.users.inactive}`;
}
